import React, { useState, useRef, useMemo } from "react";
import {
  Eye,
  Edit3,
  Bold,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Table as TableIcon,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { countWords, countPlaceholders } from "../../../services/draftsService";

interface MarkdownEditorPreviewProps {
  content: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  readOnly?: boolean;
  targetWordCount?: number;
  requirementTitle?: string;
}

export const MarkdownEditorPreview: React.FC<MarkdownEditorPreviewProps> = ({
  content,
  onChange,
  placeholder = "Write or synthesize your proposal section narrative here... Use Markdown for headers (###), bullet points, and emphasis.",
  minRows = 10,
  readOnly = false,
  targetWordCount,
}) => {
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const wordCount = useMemo(() => countWords(content), [content]);
  const actionItemsCount = useMemo(() => countPlaceholders(content), [content]);

  // Insert markdown syntax at cursor position
  const insertFormatting = (prefix: string, suffix = "", defaultText = "") => {
    if (!textareaRef.current) return;
    const el = textareaRef.current;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = content.substring(start, end) || defaultText;
    const replacement = `${prefix}${selected}${suffix}`;
    const newContent = content.substring(0, start) + replacement + content.substring(end);
    onChange(newContent);

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(
          start + prefix.length,
          start + prefix.length + selected.length,
        );
      }
    }, 0);
  };

  // Budget calculations
  const budgetRatio = targetWordCount && targetWordCount > 0 ? wordCount / targetWordCount : 0;
  const isOverBudget = budgetRatio > 1.1;
  const isOnTrack = budgetRatio >= 0.7 && budgetRatio <= 1.1;
  const isUnderBudget = budgetRatio < 0.5 && wordCount > 0;

  // Lightweight safe preview renderer
  const renderPreviewElements = (raw: string) => {
    if (!raw.trim()) {
      return (
        <div className="py-8 text-center text-xs text-slate-400 italic">
          No content written yet. Switch to Edit or click Synthesize AI to generate a response.
        </div>
      );
    }

    const lines = raw.split("\n");
    const elements: React.ReactNode[] = [];
    let tableBuffer: string[] = [];
    let listBuffer: string[] = [];

    const flushTable = (key: string) => {
      if (tableBuffer.length === 0) return;
      const rows = tableBuffer
        .map((line) =>
          line
            .split("|")
            .map((c) => c.trim())
            .filter(
              (c, idx, arr) => !(idx === 0 && c === "") && !(idx === arr.length - 1 && c === ""),
            ),
        )
        .filter((r) => r.length > 0 && !r.every((c) => /^[-:]+$/.test(c)));

      if (rows.length > 0) {
        const headerRow = rows[0] || [];
        const bodyRows = rows.slice(1);
        elements.push(
          <div
            key={`table-${key}`}
            className="my-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800"
          >
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-100 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <tr>
                  {headerRow.map((h, i) => (
                    <th key={i} className="border-b border-slate-200 p-2 dark:border-slate-700">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {bodyRows.map((r, ri) => (
                  <tr key={ri} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40">
                    {r.map((cell, ci) => (
                      <td key={ci} className="p-2 text-slate-600 dark:text-slate-300">
                        {renderInlineFormatting(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
      }
      tableBuffer = [];
    };

    const flushList = (key: string) => {
      if (listBuffer.length === 0) return;
      elements.push(
        <ul
          key={`list-${key}`}
          className="my-2 list-inside list-disc space-y-1 text-xs text-slate-700 dark:text-slate-300"
        >
          {listBuffer.map((li, i) => (
            <li key={i}>{renderInlineFormatting(li)}</li>
          ))}
        </ul>,
      );
      listBuffer = [];
    };

    const renderInlineFormatting = (text: string): React.ReactNode => {
      // Split on [ACTION NEEDED: ...]
      const parts = text.split(/(\[ACTION NEEDED:[^\]]+\])/gi);
      return parts.map((part, idx) => {
        if (/^\[ACTION NEEDED:[^\]]+\]$/i.test(part)) {
          return (
            <span
              key={idx}
              className="inline-flex items-center gap-1 mx-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900 border border-amber-300 dark:bg-amber-950/60 dark:border-amber-800 dark:text-amber-200"
            >
              <AlertTriangle className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
              <span>{part}</span>
            </span>
          );
        }

        // Bold formatting **text**
        const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
        return (
          <span key={idx}>
            {boldParts.map((bPart, bIdx) => {
              if (bPart.startsWith("**") && bPart.endsWith("**")) {
                return (
                  <strong key={bIdx} className="font-bold text-slate-900 dark:text-white">
                    {bPart.slice(2, -2)}
                  </strong>
                );
              }
              return bPart;
            })}
          </span>
        );
      });
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Table lines
      if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
        flushList(`before-table-${index}`);
        tableBuffer.push(trimmed);
        return;
      }
      flushTable(`line-${index}`);

      // List item
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        listBuffer.push(trimmed.slice(2));
        return;
      }
      flushList(`line-${index}`);

      // Headings
      if (trimmed.startsWith("### ")) {
        elements.push(
          <h3
            key={`h3-${index}`}
            className="mt-4 mb-2 text-sm font-bold text-slate-900 dark:text-white border-b border-slate-100 pb-1 dark:border-slate-800"
          >
            {renderInlineFormatting(trimmed.slice(4))}
          </h3>,
        );
        return;
      }
      if (trimmed.startsWith("## ")) {
        elements.push(
          <h2
            key={`h2-${index}`}
            className="mt-5 mb-2 text-base font-bold text-slate-900 dark:text-white"
          >
            {renderInlineFormatting(trimmed.slice(3))}
          </h2>,
        );
        return;
      }
      if (trimmed.startsWith("# ")) {
        elements.push(
          <h1
            key={`h1-${index}`}
            className="mt-6 mb-3 text-lg font-black text-slate-900 dark:text-white"
          >
            {renderInlineFormatting(trimmed.slice(2))}
          </h1>,
        );
        return;
      }

      // Blockquote
      if (trimmed.startsWith("> ")) {
        elements.push(
          <blockquote
            key={`quote-${index}`}
            className="my-2 border-l-2 border-[#2ba5b5] bg-teal-50/40 p-2.5 text-xs text-slate-700 italic dark:bg-teal-950/20 dark:text-slate-300 rounded-r-md"
          >
            {renderInlineFormatting(trimmed.slice(2))}
          </blockquote>,
        );
        return;
      }

      // Blank line
      if (!trimmed) {
        elements.push(<div key={`blank-${index}`} className="h-2" />);
        return;
      }

      // Standard Paragraph
      elements.push(
        <p
          key={`p-${index}`}
          className="my-1.5 text-xs leading-relaxed text-slate-700 dark:text-slate-300"
        >
          {renderInlineFormatting(trimmed)}
        </p>,
      );
    });

    flushTable("final");
    flushList("final");
    return elements;
  };

  return (
    <div className="space-y-2">
      {/* Editor & Toolbar Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {/* Formatting Actions (Edit mode only) */}
        {mode === "edit" && !readOnly ? (
          <div className="flex flex-wrap items-center gap-1 text-slate-600 dark:text-slate-300">
            <button
              type="button"
              onClick={() => insertFormatting("**", "**", "bold text")}
              className="btn-animate rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Bold (**text**)"
              aria-label="Format bold"
            >
              <Bold className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => insertFormatting("### ", "\n", "Heading")}
              className="btn-animate rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Heading 3 (### Heading)"
              aria-label="Format heading"
            >
              <Heading3 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => insertFormatting("- ", "\n", "List item")}
              className="btn-animate rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Bullet List (- item)"
              aria-label="Format bullet list"
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => insertFormatting("1. ", "\n", "First item")}
              className="btn-animate rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Numbered List (1. item)"
              aria-label="Format numbered list"
            >
              <ListOrdered className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => insertFormatting("> ", "\n", "Quote text")}
              className="btn-animate rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Blockquote (> text)"
              aria-label="Format quote"
            >
              <Quote className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() =>
                insertFormatting(
                  "\n| Feature | Specification |\n|---|---|\n| Standard | High Precision |\n",
                  "",
                )
              }
              className="btn-animate rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Table"
              aria-label="Format table"
            >
              <TableIcon className="h-3.5 w-3.5" />
            </button>

            <span className="mx-1 h-3.5 w-[1px] bg-slate-200 dark:bg-slate-700" />

            <button
              type="button"
              onClick={() =>
                insertFormatting("[ACTION NEEDED: ", "]", "Specify required information")
              }
              className="btn-animate inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300"
              title="Insert Action Item Tag"
            >
              <AlertTriangle className="h-3 w-3" />
              <span>Action Tag</span>
            </button>
          </div>
        ) : (
          <span className="text-xs font-semibold text-slate-500">
            {mode === "preview" ? "Rendered Response Preview" : "Draft Editor"}
          </span>
        )}

        {/* Right side: Word Meter & Edit/Preview Toggle */}
        <div className="flex items-center gap-3">
          {/* Word count & budget badge */}
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium text-slate-600 dark:text-slate-400">
              <strong className="text-slate-900 dark:text-white">{wordCount}</strong>
              {targetWordCount ? (
                <>
                  <span className="text-slate-400"> / {targetWordCount} target</span>
                  <span className="text-[11px] text-slate-400">
                    {" "}
                    ({Math.round(budgetRatio * 100)}%)
                  </span>
                </>
              ) : (
                <span className="text-slate-400"> words</span>
              )}
            </span>

            {targetWordCount && (
              <>
                {isOverBudget && (
                  <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                    Over Budget (+{wordCount - targetWordCount}w)
                  </span>
                )}
                {isOnTrack && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                    <CheckCircle2 className="h-2.5 w-2.5" />
                    <span>On Track</span>
                  </span>
                )}
                {isUnderBudget && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                    Under Target
                  </span>
                )}
              </>
            )}

            {actionItemsCount > 0 && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 border border-amber-200 dark:bg-amber-950/50 dark:border-amber-800 dark:text-amber-300">
                {actionItemsCount} Action{actionItemsCount > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Mode Segmented Control */}
          <div className="flex items-center rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800 text-xs">
            <button
              type="button"
              onClick={() => setMode("edit")}
              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold transition-all ${
                mode === "edit"
                  ? "bg-white text-[#2ba5b5] shadow-xs dark:bg-slate-700 dark:text-teal-300"
                  : "text-slate-500 hover:text-slate-900 dark:text-slate-400"
              }`}
            >
              <Edit3 className="h-3 w-3" />
              <span>Edit</span>
            </button>
            <button
              type="button"
              onClick={() => setMode("preview")}
              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold transition-all ${
                mode === "preview"
                  ? "bg-white text-[#2ba5b5] shadow-xs dark:bg-slate-700 dark:text-teal-300"
                  : "text-slate-500 hover:text-slate-900 dark:text-slate-400"
              }`}
            >
              <Eye className="h-3 w-3" />
              <span>Preview</span>
            </button>
          </div>
        </div>
      </div>

      {/* Editor Body */}
      {mode === "edit" ? (
        <textarea
          ref={textareaRef}
          rows={minRows}
          value={content}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          className="w-full rounded-xl border border-slate-200 bg-white p-3.5 text-xs font-mono leading-relaxed text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        />
      ) : (
        <div className="min-h-[220px] rounded-xl border border-slate-200 bg-white/80 p-4 dark:border-slate-800 dark:bg-slate-900/60 shadow-2xs">
          {renderPreviewElements(content)}
        </div>
      )}
    </div>
  );
};
